// Pure layout for the native chart (DESIGN-SPEC §6.20): "nice" ticks with the top
// above the largest value, unrotated 12 px labels, horizontal bars for long labels.

export function niceScale(min: number, max: number, target = 4, zero = true) {
  const low = zero ? Math.min(0, min) : min;
  const high = zero ? Math.max(0, max) : max;
  const span = high - low || Math.abs(high) || 1;
  const raw = span / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * magnitude >= raw) ?? 10) * magnitude;
  let top = Math.ceil(high / step) * step;
  // The top tick sits above the largest value, never on it.
  if (high > 0 && top <= high) top += step;
  let bottom = Math.floor(low / step) * step;
  // Off-zero domains also leave room below the smallest value.
  if (!zero && bottom >= low) bottom -= step;
  const ticks: number[] = [];
  for (let value = bottom; value <= top + step / 1000; value += step)
    ticks.push(Number(value.toPrecision(12)));
  return { ticks, bottom, top, step };
}

/**
 * The value domain for a chart. Bars always keep a zero baseline. A line whose values
 * all sit well above zero (its minimum at least ~25% of its maximum) gets a nice domain
 * around the data instead of being squashed into the top of the plot.
 */
export function valueDomain(
  type: 'bar' | 'line',
  values: readonly number[],
  stackedMin = 0,
  stackedMax = 0,
) {
  const lo = Math.min(...values, ...(stackedMin ? [stackedMin] : []));
  const hi = Math.max(...values, ...(stackedMax ? [stackedMax] : []));
  const offZero = type === 'line' && lo > 0 && lo >= 0.25 * hi && hi > lo;
  return niceScale(lo, hi, 4, !offZero);
}

export const LONG_LABEL = 12;
export const AXIS_FONT = 12;

/** Horizontal bars when a category label is long; otherwise vertical columns/lines. */
export function isHorizontal(type: 'bar' | 'line', labels: readonly string[]) {
  return type === 'bar' && labels.some((label) => label.length > LONG_LABEL);
}

/** Show every n-th category label so 12 px labels never overlap or rotate. */
export function labelStride(labels: readonly string[], slotWidth: number) {
  const widest = Math.max(1, ...labels.map((label) => label.length)) * 7 + 8;
  return Math.max(1, Math.ceil(widest / Math.max(slotWidth, 1)));
}
